"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { ArrowRightMini, MagnifyingGlassMini, XMark } from "@medusajs/icons"
import { Button, Input, Text, clx, useToggleState } from "@medusajs/ui"
import { Fragment, useEffect, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useT } from "i18n/client"
import { meilisearchQuery } from "@lib/data/search/meilisearch"
import SearchResultItem from "./search-result-item"
import { SearchResult } from "./search-result-item"

const SearchBar = () => {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useT("main")

  useEffect(() => {
    const searchProducts = async () => {
      if (query.length < 2) {
        setResults([])
        return
      }

      setIsLoading(true)
      try {
        const searchResults = await meilisearchQuery(query, 6)
        
        setResults(searchResults.map((product: any) => ({
          id: product.id,
          title: product.title,
          description: product.description,
          handle: product.handle,
          thumbnail: product.thumbnail,
          metadata: product.metadata,
        })))

        console.log(searchResults[0])
      } catch (error) {
        console.error("Search error:", error)
      } finally {
        setIsLoading(false)
      }
    }

    const debounce = setTimeout(() => {
      searchProducts()
    }, 300)

    return () => clearTimeout(debounce)
  }, [query])

  return (
    <div className="relative w-full">
      <Popover className="relative w-full">
        {({ open, close }) => (
          <>
            <div className="relative w-full">
              <MagnifyingGlassMini className="absolute left-4 text-ui-fg-muted" />
              <Input
                type="search"
                placeholder={t("search.placeholder", "Search products...")}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setIsOpen(true)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && query.length >= 2) {
                    window.location.href = `/search?q=${encodeURIComponent(query)}`
                  }
                }}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 150)}
                className="w-full pl-10 focus:border-ui-border-interactive"
              />
              {query && (
                <Button
                  variant="transparent"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setQuery("")}
                >
                  <XMark />
                </Button>
              )}
            </div>

            <Transition
              show={isOpen && query.length >= 2}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute z-10 w-full mt-2 bg-white border border-ui-border-base rounded-lg shadow-lg overflow-hidden">
                <div className="p-4">
                  {isLoading ? (
                    <div className="flex justify-center py-4">
                      <Text>{t("search.loading", "Loading...")}</Text>
                    </div>
                  ) : results.length > 0 ? (
                    <div className="space-y-4">
                      {results.map((result) => (
                        <SearchResultItem
                          key={result.id}
                          product={result}
                          onClick={() => close()}
                        />
                      ))}
                      <div className="pt-2 border-t border-ui-border-base">
                        <LocalizedClientLink 
                          href={`/search?q=${encodeURIComponent(query)}`}
                          className="flex items-center justify-between p-2 hover:bg-ui-bg-base-hover rounded-md"
                          onClick={() => close()}
                        >
                          <Text>{t("search.view_all", "View all results")}</Text>
                          <ArrowRightMini />
                        </LocalizedClientLink>
                      </div>
                    </div>
                  ) : query.length >= 2 ? (
                    <div className="py-4 text-center">
                      <Text>{t("search.no_results", "No results found")}</Text>
                    </div>
                  ) : null}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  )
}

export default SearchBar